import { useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  message,
  Alert,
  Radio,
} from "antd";
import { UploadOutlined, CloseCircleFilled } from "@ant-design/icons";
import api from "../../../config/axiosInstance";
import { API } from "../../../config/API";
import { TEMPLATES, TemplateLang } from "../templates";

const MAX_FILE_SIZE_MB = 25;

function formatSize(bytes: number) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function CreateModal(props: any) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [templateKey, setTemplateKey] = useState<string | undefined>(undefined);
  const [language, setLanguage] = useState<TemplateLang>("en");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const applyTemplate = (key: string | undefined, lang: TemplateLang) => {
    if (!key) return;
    const tmpl = TEMPLATES.find((t) => t.key === key);
    if (!tmpl) return;
    const content = tmpl.content[lang];
    if (!content) return;
    form.setFieldsValue({
      subject: content.subject,
      body: content.body,
    });
  };

  const onTemplateChange = (key: string | undefined) => {
    setTemplateKey(key);
    if (key) applyTemplate(key, language);
  };

  const onLanguageChange = (lang: TemplateLang) => {
    setLanguage(lang);
    if (templateKey) applyTemplate(templateKey, lang);
  };

  const onPickFile = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size / 1024 / 1024 >= MAX_FILE_SIZE_MB) {
      message.error(`Attachment must be smaller than ${MAX_FILE_SIZE_MB}MB`);
      e.target.value = "";
      return;
    }
    setFile(f);
    e.target.value = "";
  };

  const removeFile = () => setFile(null);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const formData = new FormData();
      formData.append("subject", String(values.subject || "").trim());
      formData.append("body", values.body || "");
      formData.append("recipient_type", values.recipient_type);
      if (file) formData.append("attachment", file);

      const res: any = await api.post(
        API.RELEASE_NOTIFICATIONS_SEND,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,
        }
      );

      const data = res?.data?.data;
      if (data) {
        const total = data.email_count || 0;
        message.success(
          `Queued for delivery to ${total} recipient(s). Check the list for progress.`
        );
        form.resetFields();
        setFile(null);
        setTemplateKey(undefined);
        setLanguage("en");
        props.onSuccess && props.onSuccess();
      } else {
        message.error("Failed to send notification");
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send notification";
      message.error(msg);
      console.error("Create release notification error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const isImage = file && file.type && file.type.startsWith("image/");
  const previewUrl = isImage ? URL.createObjectURL(file as File) : null;

  return (
    <Modal
      title="Send Release Notification"
      open={props.open}
      onCancel={() => {
        if (submitting) return;
        form.resetFields();
        setFile(null);
        setTemplateKey(undefined);
        setLanguage("en");
        props.onClose();
      }}
      onOk={handleSubmit}
      okText={submitting ? "Sending..." : "Send Email"}
      confirmLoading={submitting}
      maskClosable={!submitting}
      width={680}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Recipients are gathered automatically based on the selected audience. Duplicate emails are removed before sending."
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ recipient_type: "all" }}
      >
        <Form.Item
          name="recipient_type"
          label="Recipient Audience"
          rules={[{ required: true, message: "Please choose audience" }]}
        >
          <Select>
            <Select.Option value="all">
              All Users (App users + Epaper users)
            </Select.Option>
            <Select.Option value="epaper">Epaper Users only</Select.Option>
          </Select>
        </Form.Item>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            flexWrap: "wrap",
            marginBottom: 8,
          }}
        >
          <Form.Item
            label="Template"
            style={{ flex: "1 1 280px", marginBottom: 0 }}
            tooltip="Pre-fills subject & body. You can still edit after applying."
          >
            <Select
              allowClear
              placeholder="Choose a template (optional)"
              value={templateKey}
              onChange={onTemplateChange}
              options={TEMPLATES.map((t) => ({
                value: t.key,
                label: t.label,
              }))}
            />
          </Form.Item>
          <Form.Item label="Language" style={{ marginBottom: 0 }}>
            <Radio.Group
              value={language}
              onChange={(e) => onLanguageChange(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="en">English</Radio.Button>
              <Radio.Button value="ml">മലയാളം</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </div>

        <Form.Item
          name="subject"
          label="Subject"
          rules={[
            { required: true, message: "Subject is required" },
            { max: 255, message: "Subject must be under 255 characters" },
          ]}
        >
          <Input placeholder="e.g. Suprabhaatham App v2.0 — What's new" />
        </Form.Item>

        <Form.Item
          name="body"
          label="Body"
          rules={[{ required: true, message: "Body is required" }]}
        >
          <Input.TextArea
            rows={6}
            placeholder="Write your release notes / update message here as plain text."
          />
        </Form.Item>

        <Form.Item
          label="Attachment (optional)"
          style={{ marginBottom: 0 }}
        >
          <input
            type="file"
            ref={inputRef}
            style={{ display: "none" }}
            onChange={onFileChange}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Button icon={<UploadOutlined />} onClick={onPickFile}>
              {file ? "Replace File" : "Choose File"}
            </Button>
            {file ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 6,
                  background: "#fafafa",
                  maxWidth: "100%",
                }}
              >
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="preview"
                    style={{
                      width: 32,
                      height: 32,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                ) : null}
                <span
                  style={{
                    fontSize: 13,
                    color: "#333",
                    maxWidth: 300,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={file.name}
                >
                  {file.name}
                </span>
                <span style={{ fontSize: 12, color: "#888" }}>
                  {formatSize(file.size)}
                </span>
                <CloseCircleFilled
                  style={{ color: "#bbb", cursor: "pointer" }}
                  onClick={removeFile}
                />
              </div>
            ) : null}
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
            Max {MAX_FILE_SIZE_MB} MB. PDF, image, or any file type.
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default CreateModal;
